import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, lastValueFrom } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { User } from '../models/user.model';
import { API_ENDPOINTS, buildApiUrl, APP_CONSTANTS, USER_ROLES } from '../constants';
import { AuthStateService } from './auth-state.service';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  success: boolean;
  message?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private http: HttpClient,
    private router: Router,
    private authStateService: AuthStateService,
    private apiService: ApiService,
    private storageService: StorageService
  ) {}

  // Authentication state
  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    return this.storageService.getItem(APP_CONSTANTS.AUTH_TOKEN);
  }

  getUserRole(): string | null {
    const loginData = this.getLoginData();
    const userType = loginData?.user?.user_type;
    if (!userType) return null;

    // Map API user types to app role constants
    switch (userType.toLowerCase()) {
      case 'super_admin':
      case 'admin':
        return USER_ROLES.ADMIN;
      case 'manager':
        return USER_ROLES.MANAGER;
      case 'customer':
        return USER_ROLES.CUSTOMER;
      case 'collection_agent':
      case 'collection-agent':
        return USER_ROLES.COLLECTION_AGENT;
      default:
        return USER_ROLES.CUSTOMER;
    }
  }

  getCurrentUser() {
    const loginData = this.getLoginData();
    return loginData?.user || null;
  }

  private getLoginData() {
    const loginDataStr = this.storageService.getItem(APP_CONSTANTS.LOGINDATA);
    return loginDataStr ? JSON.parse(loginDataStr) : null;
  }

  /**
   * Authenticate user with email and password
   * @param email User email
   * @param password User password
   * @returns Promise<User> User data on success
   */
   login(credentials:any) {
         
      return this.apiService.post(API_ENDPOINTS.AUTH.LOGIN,credentials)
    }

  /**
   * Initiate password reset process
   * @param email User email for password reset
   * @returns Promise<ForgotPasswordResponse> Reset confirmation
   */
   resetPassword(payload: { email: string }): Observable<any> {
      return this.apiService.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, payload).pipe(
        catchError(err => throwError(() => err))
      );
    }

  /**
   * Logout user and clear all stored data
   * Follows business rules: set checkAuth to false, clear local/session storage, navigate to login
   */
  logout(): void {
    // Clear authentication data
    this.storageService.removeItem(APP_CONSTANTS.AUTH_TOKEN);
    this.storageService.removeItem('current_user');
    this.storageService.removeItem(APP_CONSTANTS.AUTHENTICATED);

    // Clear session storage as per business rules
    sessionStorage.clear();

    // Update authentication state
    this.authStateService.clearAuthState();

    // Navigate to login page and replace URL to prevent back navigation
    this.router.navigate(['/auth/login'], { replaceUrl: true });
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      if (error.error && typeof error.error === 'object' && error.error.message) {
        errorMessage = error.error.message;
      } else {
        // Handle different HTTP status codes
        switch (error.status) {
          case 400:
            errorMessage = 'Invalid request. Please check your input.';
            break;
          case 401:
            errorMessage = 'Invalid credentials. Please try again.';
            break;
          case 403:
            errorMessage = 'Access forbidden.';
            break;
          case 404:
            errorMessage = 'Service not found.';
            break;
          case 429:
            errorMessage = 'Too many requests. Please try again later.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = `Error ${error.status}: ${error.statusText}`;
        }
      }
    }

    return throwError(new Error(errorMessage));
  }
}
